import { useCallback, useRef } from 'react';

import { widgetSDK } from '../../sdk/widgetSDK';
import { InitialChatbotState } from '../../types';
import { fetchInitialChatbotState } from '../api/fetchInitialChatbotState';
import { useFedopsLogger } from '../providers/fedopsLogger';

const initialURLSearchParams = new URLSearchParams(window.location.search);

type GetInitialChatbotState = {
  isParamsDifferent: (params: Map<string, string>) => boolean;
  getInitialChatbotState: (params?: Map<string, string>) => Promise<InitialChatbotState>;
};

export const useGetInitialChatbotState = (): GetInitialChatbotState => {
  const fedopsLogger = useFedopsLogger();
  const startChatParams = useRef<Map<string, string>>(new Map(initialURLSearchParams));

  const isParamsDifferent = useCallback((params: Map<string, string>): boolean => {
    for (const [key, value] of Array.from(params)) {
      if (startChatParams.current.get(key) !== value) {
        return true;
      }
    }

    return false;
  }, []);

  const getInitialChatbotState = useCallback(
    async (params?: Map<string, string>) => {
      // if there are params - we do 'start-chat' with these new params and save them
      if (params) {
        startChatParams.current = params;
      }

      const context = await widgetSDK.getContext();

      fedopsLogger.interactionStarted('fetching-initial-chatbot-state');
      const initialChatbotState = await fetchInitialChatbotState(
        Object.fromEntries(startChatParams.current),
        context,
      );
      fedopsLogger.interactionEnded('fetching-initial-chatbot-state');

      return initialChatbotState;
    },
    [fedopsLogger],
  );

  return { getInitialChatbotState, isParamsDifferent };
};
